import { parseFrameText } from './frames';
import type { ClientFrame, ServerFrame } from './frames';

/**
 * Lifecycle of one realtime connection. idle: not started; connecting: socket opening; authing:
 * socket open, auth frame sent, waiting for the server snapshot; live: snapshot received,
 * frames flow; reconnecting: the socket dropped and a fresh-token reconnect is scheduled or in
 * flight; closed: stopped for good (user stop, removal, or reconnect attempts exhausted).
 */
export type RealtimeStatus = 'idle' | 'connecting' | 'authing' | 'live' | 'reconnecting' | 'closed';

/** Application close codes of the protocol (ws.go, lobby.go). */
const CLOSE_KICKED = 4403;
const CLOSE_MEETING_ENDED = 4406;
const CLOSE_JOIN_DENIED = 4407;
const CLOSE_LOBBY_TIMEOUT = 4400;

/**
 * Escalating reconnect backoff (a fresh token per attempt): 1s..15s absorb short blips, then
 * every later attempt waits the 30s tail. The tail repeats until the schedule spans
 * RECONNECT_TOTAL_BUDGET_MS of scheduled failed-attempt waiting (~10 minutes), so a Wi-Fi
 * switch or a VPN blip rides out instead of killing the session after a couple of seconds.
 */
const RECONNECT_BACKOFF_STEPS_MS: readonly number[] = [1_000, 2_000, 4_000, 8_000, 15_000];
/** Delay of every reconnect attempt once the escalation above tops out. */
const RECONNECT_TAIL_DELAY_MS = 30_000;
/**
 * Total scheduled waiting across consecutive failed reconnect attempts before the client gives
 * up (exhausted). A successful authentication resets the attempt counter, so only one continuous
 * outage can spend this budget.
 */
const RECONNECT_TOTAL_BUDGET_MS = 600_000;

/** Builds the production schedule: the escalation, then the tail repeating up to the budget. */
function defaultReconnectSchedule(): readonly number[] {
  const schedule: number[] = [...RECONNECT_BACKOFF_STEPS_MS, RECONNECT_TAIL_DELAY_MS];
  let total = schedule.reduce((sum, delay) => sum + delay, 0);
  while (total + RECONNECT_TAIL_DELAY_MS <= RECONNECT_TOTAL_BUDGET_MS) {
    schedule.push(RECONNECT_TAIL_DELAY_MS);
    total += RECONNECT_TAIL_DELAY_MS;
  }
  return schedule;
}

/** Production reconnect schedule (exported for tests): 24 attempts summing to the budget. */
export const DEFAULT_RECONNECT_SCHEDULE_MS: readonly number[] = defaultReconnectSchedule();

/** Minimal surface of a WebSocket the client uses; tests substitute a fake. */
export interface WebSocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: (() => void) | null;
  onmessage: ((event: { readonly data: unknown }) => void) | null;
  onclose: ((event: { readonly code: number; readonly reason: string }) => void) | null;
  onerror: (() => void) | null;
}

export type SocketFactory = (url: string) => WebSocketLike;

/** Events the client emits; every server frame is forwarded verbatim after parsing. */
export interface RealtimeEvents {
  status: RealtimeStatus;
  frame: ServerFrame;
  /** Close 4403: removed by a moderator; no reconnect follows. */
  removed: undefined;
  /**
   * Close 4406 (the unified room bars): a staff member ended the meeting for everyone; the
   * meeting_ended frame always precedes the close. No reconnect - the room session is over.
   */
  meetingEnded: undefined;
  /**
   * Close 4407 (CR-14): a staff member denied this lobby join. The lobby_denied frame usually
   * arrives first; the close is the authoritative terminal signal either way. No reconnect.
   */
  denied: undefined;
  /**
   * Close 4400 right after error{lobby_timeout} (CR-14): the 5-minute lobby wait crossed its
   * deadline. Distinguished from a plain auth-window 4400 by the error frame received earlier
   * on the same connection. No reconnect - the server will re-hold, not resume.
   */
  lobbyTimeout: undefined;
  /** Reconnect attempts exhausted; the UI shows the disconnected state. */
  exhausted: undefined;
}

type Listener<K extends keyof RealtimeEvents> = (payload: RealtimeEvents[K]) => void;

export interface RealtimeClientOptions {
  /** ws(s):// URL of the control service's /ws endpoint (same origin through the Vite proxy). */
  readonly url: string;
  /**
   * Returns the join JWT for the auth frame. `firstAuth` is true only for the very first
   * authentication of this client's lifetime - the token the join flow already minted is
   * served then. Every later call (a reconnect) must mint a fresh one: the join POST is
   * idempotent, so "re-run the join" is exactly this.
   */
  readonly getToken: (firstAuth: boolean) => Promise<string>;
  /** Socket factory override for tests. Defaults to the global WebSocket. */
  readonly socketFactory?: SocketFactory | undefined;
  /**
   * Delays (ms) before the consecutive reconnect attempts of one outage; when the listed
   * entries are spent the client gives up (exhausted). Defaults to the production escalating
   * schedule spanning RECONNECT_TOTAL_BUDGET_MS; tests pass all-zero schedules.
   */
  readonly reconnectScheduleMs?: readonly number[] | undefined;
}

/**
 * The realtime WS client: connects to /ws, sends auth as the first frame, then forwards typed
 * server frames. It tracks the per-connection delivery `seq` (a gap means missed frames ->
 * reconnect), and on any non-user close but the terminal application closes it re-mints the
 * token and reconnects through an escalating backoff (30s tail, ~10 minutes of scheduled
 * attempts per outage; a browser 'online' event cuts the pending wait short), before reporting
 * exhausted. A successful authentication resets the attempt counter, so only one continuous
 * outage can spend the budget. Terminal closes: removal (4403, emits removed), a lobby deny
 * (4407, emits denied) and a lobby wait that crossed its deadline (4400 after
 * error{lobby_timeout}, emits lobbyTimeout).
 */
export class RealtimeClient {
  private readonly options: RealtimeClientOptions;
  private readonly factory: SocketFactory;
  private readonly schedule: readonly number[];
  private readonly listeners = new Map<keyof RealtimeEvents, Set<Listener<never>>>();

  private socket: WebSocketLike | null = null;
  private status: RealtimeStatus = 'idle';
  private lastSeq: number | null = null;
  /** False until the first frame of the current connection seeds the watermark. */
  private seqSeeded = false;
  private attempts = 0;
  private stopped = false;
  /** Flips once a connection has authenticated successfully; drives getToken's firstAuth. */
  private everAuthenticated = false;
  /**
   * Set when error{lobby_timeout} arrives (CR-14); the 4400 close that always follows is then
   * terminal instead of a reconnectable auth-window expiry.
   */
  private lobbyTimedOut = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Guards against scheduling two reconnects from one dying socket. */
  private reconnectPending = false;
  /** Whether the window 'online' listener is currently wired (start wires, stop unwires). */
  private onlineWired = false;

  constructor(options: RealtimeClientOptions) {
    this.options = options;
    this.factory =
      options.socketFactory ??
      ((url: string): WebSocketLike => new WebSocket(url) as unknown as WebSocketLike);
    const injected = options.reconnectScheduleMs;
    this.schedule =
      injected !== undefined && injected.length > 0 ? injected : DEFAULT_RECONNECT_SCHEDULE_MS;
  }

  getStatus(): RealtimeStatus {
    return this.status;
  }

  on<K extends keyof RealtimeEvents>(event: K, listener: Listener<K>): () => void {
    const set = this.listeners.get(event) ?? new Set();
    set.add(listener as Listener<never>);
    this.listeners.set(event, set);
    return () => {
      set.delete(listener as Listener<never>);
    };
  }

  /** Opens the connection. A second start while running is a no-op; after closed it restarts. */
  start(): void {
    if (this.status !== 'idle' && this.status !== 'closed') {
      return;
    }
    this.stopped = false;
    this.attempts = 0;
    this.wireOnline();
    this.open(true);
  }

  /** User-initiated stop (leave, unmount): close and never reconnect. */
  stop(): void {
    this.stopped = true;
    this.unwireOnline();
    this.clearReconnectTimer();
    const socket = this.socket;
    this.socket = null;
    if (socket !== null) {
      // onclose stays wired: the server's 4403 can arrive after this local teardown (the
      // media drop may flip the page before the close frame is processed), and the removal
      // must still surface. handleClose routes a late kick to the removed event.
      socket.onmessage = null;
      socket.onerror = null;
      socket.onopen = null;
      socket.close(1000, 'client stop');
    }
    this.setStatus('closed');
  }

  /** Sends a client frame; silently dropped unless the connection is live. */
  send(frame: ClientFrame): void {
    const socket = this.socket;
    if (socket === null || this.status !== 'live' || socket.readyState !== 1) {
      return;
    }
    socket.send(JSON.stringify(frame));
  }

  private open(fresh: boolean): void {
    this.setStatus(fresh ? 'connecting' : 'reconnecting');
    this.seqSeeded = false;
    this.lobbyTimedOut = false;
    const socket = this.factory(this.options.url);
    this.socket = socket;
    this.reconnectPending = false;
    socket.onopen = () => {
      if (this.socket !== socket || this.stopped) {
        return;
      }
      this.setStatus('authing');
      void this.authenticate(socket);
    };
    socket.onmessage = (event: { readonly data: unknown }) => {
      this.handleMessage(socket, event.data);
    };
    socket.onclose = (event: { readonly code: number; readonly reason: string }) => {
      this.handleClose(socket, event.code);
    };
    socket.onerror = () => {
      // The close event follows; nothing to do here but avoid unhandled noise.
    };
  }

  private async authenticate(socket: WebSocketLike): Promise<void> {
    let token: string;
    try {
      token = await this.options.getToken(!this.everAuthenticated);
    } catch {
      // Token minting failed: the socket would time out server-side anyway; drop it and let
      // the close path schedule the next attempt.
      socket.close(4000, 'token mint failed');
      return;
    }
    if (this.socket !== socket || this.stopped) {
      return;
    }
    socket.send(JSON.stringify({ type: 'auth', token } satisfies ClientFrame));
  }

  private handleMessage(socket: WebSocketLike, data: unknown): void {
    if (this.socket !== socket || this.stopped) {
      return;
    }
    if (typeof data !== 'string') {
      return;
    }
    const frame = parseFrameText(data);
    if (frame === null) {
      return;
    }
    if (frame.type === 'error' && frame.code === 'lobby_timeout') {
      this.lobbyTimedOut = true;
    }
    if (!this.trackSeq(frame.seq)) {
      // Seq gap: frames were missed on this socket; resynchronise through a reconnect.
      socket.close(4000, 'seq gap');
      return;
    }
    if (this.status !== 'live') {
      // The first well-formed sequenced frame (the state snapshot) means auth succeeded.
      this.everAuthenticated = true;
      this.attempts = 0;
      this.setStatus('live');
    }
    this.emit('frame', frame);
  }

  /**
   * Advances the seq watermark. The first frame of every connection seeds it (delivery ordinals
   * are per connection, so a reconnect starts a fresh stream); afterwards it must increase by
   * exactly one per frame - a gap means frames were missed.
   */
  private trackSeq(seq: number): boolean {
    if (!this.seqSeeded) {
      this.seqSeeded = true;
      this.lastSeq = seq;
      return true;
    }
    const previous = this.lastSeq;
    if (previous === null) {
      return false;
    }
    const expected = previous + 1;
    if (seq !== expected) {
      // A duplicate or a gap both mean desynchronisation; resync through a reconnect.
      return false;
    }
    this.lastSeq = seq;
    return true;
  }

  private handleClose(socket: WebSocketLike, code: number): void {
    if (this.socket !== socket) {
      // A socket this client already abandoned (stop() tore it down, or a reconnect replaced
      // it): the only late outcomes that still matter are the terminal moderation closes -
      // their close frames may be in flight when the local teardown runs, and the removed /
      // rejected / ended screens must still show.
      if (this.stopped && code === CLOSE_KICKED) {
        this.emit('removed', undefined);
      }
      if (this.stopped && code === CLOSE_MEETING_ENDED) {
        this.emit('meetingEnded', undefined);
      }
      return;
    }
    this.socket = null;
    if (code === CLOSE_KICKED) {
      this.stopped = true;
      this.setStatus('closed');
      this.emit('removed', undefined);
      return;
    }
    if (code === CLOSE_MEETING_ENDED) {
      // The meeting_ended frame delivered the decision (and who made it); the close makes it
      // terminal. Reconnecting is meaningless: the SFU room is deleted behind the frame.
      this.stopped = true;
      this.setStatus('closed');
      this.emit('meetingEnded', undefined);
      return;
    }
    if (code === CLOSE_JOIN_DENIED) {
      // CR-14: the staff deny already delivered its lobby_denied frame; the close makes it
      // terminal. Reconnecting would re-hold a NEW join, never resume this one - the retry
      // is an explicit user action.
      this.stopped = true;
      this.setStatus('closed');
      this.emit('denied', undefined);
      return;
    }
    if (code === CLOSE_LOBBY_TIMEOUT && this.lobbyTimedOut) {
      // CR-14: the wait crossed the server's 5-minute deadline (error{lobby_timeout} arrived
      // earlier on this connection). Terminal like the deny; a plain 4400 (auth-window miss)
      // stays reconnectable and falls through.
      this.stopped = true;
      this.setStatus('closed');
      this.emit('lobbyTimeout', undefined);
      return;
    }
    if (this.stopped) {
      return;
    }
    this.scheduleReconnect();
  }

  /**
   * Browser connectivity is back mid-outage: cancel the pending backoff timer and reconnect
   * immediately instead of waiting out the slot (the big win for Wi-Fi/VPN recovery). No
   * pending timer (live, already reconnecting in flight, or exhausted) means nothing to do -
   * exhaustion revives through the hook's own online listener, not here.
   */
  private readonly handleOnline = (): void => {
    if (this.stopped || this.reconnectTimer === null) {
      return;
    }
    this.clearReconnectTimer();
    this.open(false);
  };

  private wireOnline(): void {
    if (this.onlineWired) {
      return;
    }
    this.onlineWired = true;
    window.addEventListener('online', this.handleOnline);
  }

  private unwireOnline(): void {
    if (!this.onlineWired) {
      return;
    }
    this.onlineWired = false;
    window.removeEventListener('online', this.handleOnline);
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectPending) {
      return;
    }
    const attempt = this.attempts;
    if (attempt >= this.schedule.length) {
      this.setStatus('closed');
      this.emit('exhausted', undefined);
      return;
    }
    this.attempts = attempt + 1;
    this.reconnectPending = true;
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.stopped) {
        return;
      }
      this.open(false);
    }, this.schedule[attempt] ?? 0);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private setStatus(status: RealtimeStatus): void {
    if (this.status === status) {
      return;
    }
    this.status = status;
    this.emit('status', status);
  }

  private emit<K extends keyof RealtimeEvents>(event: K, payload: RealtimeEvents[K]): void {
    for (const listener of [...(this.listeners.get(event) ?? [])]) {
      (listener as Listener<K>)(payload);
    }
  }
}

/**
 * Same-origin /ws URL: ws behind http, wss behind https. The protocol/host parameters exist for
 * tests; callers use the defaults, which read the current location.
 */
export function realtimeUrl(
  protocol: string = globalThis.location.protocol,
  host: string = globalThis.location.host,
): string {
  const scheme = protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${host}/ws`;
}
